import { AudioObj, AudioType } from "../API/AudioPlayerAPI";
import { AudioDurationSecs, ExperienceId, SenseId, SoundscapeId } from "../Data/constants";
import { getExperienceAudioLevelDefaults } from "../Data/Setup/data_experience_senses";

export const DEFAULT_GLOBAL_VOLUME_PERCENT = 75;

const AUDIO_LEVELS = getExperienceAudioLevelDefaults();

export const getAudioObjLevel = (zenseId: SenseId, experienceId: ExperienceId, audioType: AudioType) => {
    switch (audioType) {

        case AudioType.Binaural:
            // for White and Brown noise experiences, reduce Binaural audio level because it's over powering
            if (zenseId === SenseId.Sound 
                && (experienceId === SoundscapeId.WhiteNoise
                    || experienceId === SoundscapeId.BrownNoise)
                ) 
            {
                return AUDIO_LEVELS.binnauralWhiteBrownNoise;
            } else {
                return AUDIO_LEVELS.binnaural;
            }
        
        case AudioType.Music:
            return AUDIO_LEVELS.music;

        case AudioType.Soundscape:
            if (experienceId === SoundscapeId.WhiteNoise 
                || experienceId === SoundscapeId.BrownNoise)
            {
                return AUDIO_LEVELS.soundscapeWhiteBrownNoise;
            } else {
                return AUDIO_LEVELS.soundscape;
            }
        
        case AudioType.AudioBreath:
            return AUDIO_LEVELS.audioBreath;

        case AudioType.VisualBreathTrigger:
            return AUDIO_LEVELS.visualBreathTrigger;
        
        default:
            throw new Error(`Invalid type=${audioType}`);
    }
}

export function audioObjMapToArray(map: {[key: string]: AudioObj}): AudioObj[] {
    const arr: AudioObj[] = [];
    for (const audioObj of Object.values(map)) {
        arr.push(audioObj);
    }
    return arr;
}

type FocusSoundscapeTrack = 'drift' | 'expand' | 'flow' | 'ignite' | 'lift' | 'open' | 'settle';

export function isFocusSoundscapeExperience(musicTrack: string): musicTrack is FocusSoundscapeTrack {
    const focusSoundscapeTracks: FocusSoundscapeTrack[] = ['drift', 'expand', 'flow', 'ignite', 'lift', 'open', 'settle'];
    return focusSoundscapeTracks.includes(musicTrack as FocusSoundscapeTrack);
}

export function getFocusSoundscapeMusicUrl(experience: string, timeLimit: number): string {
    const { 
        drift3, drift10, drift25,
        expand3, expand10, expand25,
        flow3, flow10, flow25,
        ignite3, ignite10, ignite25,
        lift3, lift10, lift25,
        open3, open10, open25,
        settle3, settle10, settle25
    } = require("../Impl/AudioFileAssetImports");

    const focusMusic: Record<FocusSoundscapeTrack, Record<number, string>> = {
        drift: {
            [AudioDurationSecs.THREE_3_MINS]: drift3,
            [AudioDurationSecs.TEN_10_MINS]: drift10,
            [AudioDurationSecs.TWENTY_FIVE_25_MINS]: drift25,
        },
        expand: {
            [AudioDurationSecs.THREE_3_MINS]: expand3,
            [AudioDurationSecs.TEN_10_MINS]: expand10,
            [AudioDurationSecs.TWENTY_FIVE_25_MINS]: expand25,
        },
        flow: {
            [AudioDurationSecs.THREE_3_MINS]: flow3,
            [AudioDurationSecs.TEN_10_MINS]: flow10,
            [AudioDurationSecs.TWENTY_FIVE_25_MINS]: flow25,
        },
        ignite: {
            [AudioDurationSecs.THREE_3_MINS]: ignite3,
            [AudioDurationSecs.TEN_10_MINS]: ignite10,
            [AudioDurationSecs.TWENTY_FIVE_25_MINS]: ignite25,
        },
        lift: {
            [AudioDurationSecs.THREE_3_MINS]: lift3,
            [AudioDurationSecs.TEN_10_MINS]: lift10,
            [AudioDurationSecs.TWENTY_FIVE_25_MINS]: lift25,
        },
        open: {
            [AudioDurationSecs.THREE_3_MINS]: open3,
            [AudioDurationSecs.TEN_10_MINS]: open10,
            [AudioDurationSecs.TWENTY_FIVE_25_MINS]: open25,
        },
        settle: {
            [AudioDurationSecs.THREE_3_MINS]: settle3,
            [AudioDurationSecs.TEN_10_MINS]: settle10,
            [AudioDurationSecs.TWENTY_FIVE_25_MINS]: settle25,
        },
    };

    if (isFocusSoundscapeExperience(experience)) {
        switch (timeLimit) {
            case AudioDurationSecs.THREE_3_MINS:
                return focusMusic[experience][AudioDurationSecs.THREE_3_MINS];
            case AudioDurationSecs.TEN_10_MINS:
                return focusMusic[experience][AudioDurationSecs.TEN_10_MINS];
            case AudioDurationSecs.TWENTY_FIVE_25_MINS:
                return focusMusic[experience][AudioDurationSecs.TWENTY_FIVE_25_MINS];
            default:
                return focusMusic[experience][AudioDurationSecs.TWENTY_FIVE_25_MINS];
        }
    }

    return focusMusic.drift[AudioDurationSecs.THREE_3_MINS];
}
